import { durationMs } from "../charts/series";
import { durationText, localParts, onOffSpans, onTime, starts } from "../glance";
import { Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const RANGES = new Set(["6h", "24h", "7d"]);

/**
 * state_timeline: one lane per boolean value across the range, filled where it was on, with the total on-time per
 * lane and, for one lane, how many times it started (VisionPlatform widget-library-2 WLB-13).
 */
export function StateTimeline({ widget, input, now, tz }: WidgetProps) {
  const fmt = useWidgetText();
  const cfg = widget.config ?? {};
  const range = typeof cfg.range === "string" && RANGES.has(cfg.range) ? cfg.range : "24h";
  const end = now.getTime();
  const from = end - (durationMs(range) ?? 86_400_000);
  const lanes = (widget.bindings ?? [])
    .filter((b) => b.datapoint)
    .map((b) => {
      const k = key(b.device, b.datapoint ?? "");
      const h = input.history?.[k];
      const spans = onOffSpans(h, end).map((s) => ({ ...s, from: Math.max(s.from, from) }));
      return {
        k,
        name: b.label ?? input.points?.[k]?.name ?? b.datapoint ?? "",
        spans,
        on: spans.length ? durationText(onTime(spans), fmt.t) : undefined,
      };
    });
  const any = lanes.some((l) => l.spans.length);
  const first = lanes[0];
  const startCount = lanes.length === 1 && first?.spans.length ? starts(first.spans) : undefined;
  const label = any
    ? (widget.title ? `${widget.title}: ` : "") + lanes.flatMap((l) => (l.on ? [`${l.name} ${l.on}`] : [])).join(", ")
    : (widget.title ? `${widget.title}: ` : "") + `${first?.name ?? ""} ${fmt.t("state.noData")}`;
  const x = (t: number) => ((t - from) / (end - from)) * 100;
  const time = (t: number) => localParts(t, tz).hhmm;
  return (
    <Frame widget={widget} state={any ? "normal" : "empty"} title={widget.title} label={label}>
      {!any && (
        <>
          <p className="text-sm text-gray-700">{first?.name}</p>
          <p className="text-gray-700">{fmt.t("state.noData")}</p>
        </>
      )}
      {any && (
        <ul className="flex flex-1 flex-col justify-center gap-2">
          {lanes.map((l) => (
            <li key={l.k} className="grid grid-cols-[minmax(3rem,auto)_1fr_auto] items-center gap-2 text-sm">
              <span className="truncate text-gray-700">{l.name}</span>
              <div className="relative h-5 rounded bg-gray-100" aria-hidden="true">
                {l.spans
                  .filter((s) => s.on)
                  .map((s) => (
                    <div
                      key={s.from}
                      title={`${time(s.from)}–${time(s.to)}`}
                      className="absolute inset-y-0 rounded-sm bg-brand"
                      style={{
                        left: `${x(s.from).toFixed(2)}%`,
                        width: `${Math.max(0.4, x(s.to) - x(s.from)).toFixed(2)}%`,
                      }}
                    />
                  ))}
              </div>
              <span className="text-right font-semibold text-gray-900 tabular-nums">{l.on ?? "–"}</span>
              <ul className="sr-only">
                {l.spans
                  .filter((s) => s.on)
                  .map((s) => (
                    <li key={s.from}>{`${time(s.from)}–${time(s.to)}`}</li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {startCount !== undefined && (
        <p className="text-xs text-gray-700">
          {startCount === 0
            ? fmt.t("timeline.startsNone")
            : startCount === 1
              ? fmt.t("timeline.startsOne")
              : fmt.t("timeline.starts", { count: startCount })}
        </p>
      )}
    </Frame>
  );
}
